import React from 'react';
import Cropper from 'react-easy-crop';
import { Point, Area } from 'react-easy-crop/types';
import { AttachImageResult } from '@gik/core/utils/FileUtils';
import bemBlock from '@gik/core/utils/bemBlock';
import RangeSlider from '@gik/ui/RangeSlider';
import FontIcon from '@gik/ui/FontIcon';
import noop from '@gik/core/utils/noop';
import { Button } from '..';

export interface IImageCropperProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  image?: AttachImageResult;
  fallback?: React.ReactNode;
  aspect?: number; // TODO we have an AspectRatio type, we could also use that one here
  onChange?(imageData: IImageCropData): void;
}

export interface IImageCropData {
  image: string;
  crop: Point;
  zoom: number;
  rotation: number;
  croppedAreaPixels: Area;
}

export default function ImageCropper({
  image,
  aspect = null,
  fallback,
  className,
  onChange = noop,
}: IImageCropperProps) {
  const bem = bemBlock('image-cropper');

  const [crop, setCrop] = React.useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState<number>(1);
  const [rotation, setRotation] = React.useState<number>(0);
  const [imageRatio, setImageRatio] = React.useState<number>(aspect);
  const mainRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!image || !mainRef) return;
    if (aspect == null) {
      // if aspect is null automatically determine it based on the image data
      setImageRatio(image.width / image.height);
    } else if (aspect == -1) {
      // if aspect is -1 automatically determine it based on the size of the cropper container
      const boundingRect: DOMRect = mainRef.current?.getBoundingClientRect();
      setImageRatio(boundingRect.width / boundingRect.height);
    } else {
      setImageRatio(aspect);
    }
  }, [image, aspect, mainRef, setImageRatio]);

  return (
    <div className={bem(null, null, className)} ref={mainRef}>
      <div className={bem('image')}>
        {image && (
          <Cropper
            image={image.base64}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={imageRatio}
            onCropChange={setCrop}
            onCropComplete={(_croppedArea, croppedAreaPixels) => {
              onChange({
                image: image.base64,
                crop,
                zoom,
                rotation,
                croppedAreaPixels,
              });
            }}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            style={{
              // stylelint does not recognize cropAreaStyle
              // stylelint-disable-next-line
              cropAreaStyle: {
                background: `linear-gradient(to right, white 60px, transparent 60px), linear-gradient(to right, white 60px, transparent 60px), linear-gradient(to bottom, white 60px, transparent 60px), linear-gradient(to bottom, white 60px, transparent 60px)`,
                backgroundSize: '100% 5px, 100% 5px, 5px 100%, 5px 100%',
                backgroundPosition: '-30px 0%, -30px 100%, 0% -30px, 100% -30px',
                backgroundRepeat: 'repeat-x, repeat-x, repeat-y, repeat-y',
                border: 'none',
              },
            }}
            showGrid={false}
          />
        )}
        {!image && fallback}
      </div>
      {image && (
        <div className={bem('toolbar')}>
          <div className={bem('sliderWrapper')}>
            <RangeSlider className={bem('zoomSlider')} value={zoom} min={1} max={3} step={0.1} onChange={setZoom} />
          </div>
          <Button
            onClick={() => {
              setRotation(rotation >= 360 ? 0 : rotation + 90);
            }}
            variant="primary-link"
            className={bem('rotateButton')}
          >
            <FontIcon size="xl" name="refresh" />
          </Button>
        </div>
      )}
    </div>
  );
}
